import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { toast } from '../store/toastStore';
import { NOTIFICATIONS_QUERY_KEY } from '../components/NotificationCenter';
import { newReminderPops } from '../utils/reminderModel';

/** The reminder stays on screen a little longer than a normal toast (the person may be away from the keyboard). */
const REMINDER_TOAST_MS = 10_000;

/**
 * W-01 - the pop-up of a "התראה חדשה" reminder. It rides on the bell's own poll (same query key,
 * no extra request - D-236 live updates), so it appears within the poll interval of its time, and
 * a reminder that fired while the person was away pops at the next login. Shown once per firing:
 * a row that pops again ("every X minutes") has a new key.
 */
export function useReminderToasts(enabled: boolean): void {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const shown = useRef<Set<string>>(new Set());
  const { data, isSuccess } = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: () => api.getNotifications(),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    enabled,
  });

  useEffect(() => {
    if (!enabled || !isSuccess) return;
    for (const n of newReminderPops(data?.data ?? [], shown.current, 3)) {
      const text = String(n.metadata?.message ?? '');
      const creator = n.metadata?.is_self === false && n.metadata?.creator_name ? `\n${t('reminder.fromCreator', { name: n.metadata.creator_name })}` : '';
      const occurrenceId = n.metadata?.occurrence_id;
      const keep = n.metadata?.keep_until_done === true && !!occurrenceId;
      const refresh = () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      toast('info', `${t('reminder.toastPrefix')} ${text}${creator}`, {
        duration: REMINDER_TOAST_MS,
        action: {
          label: keep ? t('reminder.done') : t('reminder.gotIt'),
          onClick: () => {
            const call = keep ? api.ackReminderOccurrence(String(occurrenceId), 'done') : api.markNotificationRead(n.id);
            void call.then(refresh).catch(() => undefined);
          },
        },
      });
    }
  }, [data, isSuccess, enabled, queryClient, t]);
}
